import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminPosition } from "@/modules/chart/admin/types";

export type PositionList = Paginate<AdminPosition<true, true, true, true>>;

export const positions = createQueryKeys("admin-chart-positions", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "users,positionLevel,parent,unit";
const detailInclude = "users,positionLevel,parent,unit,roles";

export const fetchPositionsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...positions.all(page, search),
    queryFn: () =>
      adminApi
        .get<PositionList>(`/chart/positions`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchPositions = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchPositionsQueryOptions(page, search));

export const fetchPositionQueryOptions = (id?: number) =>
  queryOptions({
    ...positions.one(id),
    queryFn: () =>
      adminApi
        .get<AdminPosition<true, true, true, true, false, false, true>>(
          `/chart/positions/${id}`,
          {
            params: { include: detailInclude },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchPosition = (id?: number) =>
  useQuery(fetchPositionQueryOptions(id));

export interface PositionRequest {
  code: number;
  name: string;
  type: AdminPosition["type"];
  chart_position_level_id: number;
  grade?: number | null;
  chart_unit_id: number;
  parent_id?: number | null;
  users: number[];
  roles: number[];
  description?: string | null;
}

export const useCreatePosition = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: PositionRequest) =>
      adminApi
        .post("/chart/positions/", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: positions.all._def,
      });
    },
  });
};

export const useUpdatePosition = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: PositionRequest) =>
      adminApi
        .put(`/chart/positions/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: positions.all._def,
      });
      queryClient.invalidateQueries(positions.one(id));
    },
  });
};

export const useCreateOrUpdatePosition = (id: number | undefined) => {
  const create = useCreatePosition();
  const update = useUpdatePosition(id!);
  return id ? update : create;
};

export const useDeletePosition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`chart/positions/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: positions.all._def,
      });
      queryClient.invalidateQueries(positions.one(id));
    },
  });
};
